import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FileConfig } from 'classes-lib';
import { BullhornNamespace, LocalFileNamespace, SalesforceNamespace } from 'datasource-service-lib';
import { DocumentTypeServiceFactory } from 'document-type-picker-lib';
import { AttachmentPosition, FIRST_SECTION_BREAK, MAX_SECTION_BREAK, Namespace, SectionSourceType } from 'enums-lib';
import { ISection } from 'interfaces-lib';
import { SectionComponentBase } from '../section-component-base';
import { FileAge, FileAgeRestrictionComponent } from './attachment-restrictions/file-age-restriction/file-age-restriction.component';

@Component({
  selector: 'app-attachment-section',
  templateUrl: './attachment-section.component.html',
  styleUrls: ['./attachment-section.component.scss'],
  standalone: false,
})
export class AttachmentSectionComponent extends SectionComponentBase implements OnInit {
  // Keep this list explicit so adding another datasource later only requires updating one place.
  private static readonly NAMESPACES_WITH_UPLOAD_SOURCE_SELECTOR = [Namespace.bullhorn, Namespace.salesforce];

  @Input() fileSection: FormGroup;
  @Input() mergeSection: FormGroup;
  @Input() namespace: Namespace | string;
  @Input() set entityType(entityType: string) {
    this._entityType = entityType;
  }
  @Input() set forceEnabled(forceEnabled: boolean) {
    this._forceEnabled = forceEnabled;
    this.forceAttachmentRequired(forceEnabled);
  }

  get fileConfig(): FormGroup {
    return this.fileSection?.get('config') as FormGroup;
  }

  get fileSectionRequired(): boolean {
    return this.fileConfig.get('required').value;
  }

  get mergeConfigForm(): FormGroup {
    return this.mergeSection?.get('config') as FormGroup;
  }

  get attachmentPlacementForm(): FormGroup {
    return this.mergeConfigForm?.get('attachment-placement') as FormGroup;
  }

  get mergeStylingForm(): FormGroup {
    return this.mergeSection?.get('config')?.get('merge-styling') as FormGroup;
  }

  get attachmentFontSizeType(): string {
    return this.mergeStylingForm?.get('attachmentFontSizeType')?.value;
  }

  get attachmentPosition(): AttachmentPosition {
    return this.attachmentPlacementForm?.get('position')?.value;
  }

  get attachmentSectionBreakControl(): AbstractControl {
    return this.attachmentPlacementForm?.get('sectionBreak');
  }

  get showSectionBreakError(): boolean {
    const sectionBreakControl = this.attachmentSectionBreakControl;
    return Boolean(sectionBreakControl?.invalid && sectionBreakControl.dirty);
  }

  get showUploadSourceSelector(): boolean {
    return AttachmentSectionComponent.NAMESPACES_WITH_UPLOAD_SOURCE_SELECTOR.includes(this.namespace as Namespace);
  }

  _entityType: string;
  _forceEnabled = false;
  outputFileType: string;
  recordTerm: string;
  valid = true;
  required = false;

  fontFamilies = [
    'Arial',
    'Bell MT',
    'Bodoni MT',
    'Bookman Old Style',
    'Calibri',
    'Cambria',
    'Century Gothic',
    'Didot',
    'Garamond',
    'Georgia',
    'Gill Sans MT',
    'Goudy Old Style',
    'Helvetica',
    'Lucida Sans',
    'Tahoma',
    'Times New Roman',
    'Trebuchet MS',
    'Verdana',
  ];

  protected readonly Namespace = Namespace;
  protected readonly AttachmentPosition = AttachmentPosition;
  protected readonly FIRST_SECTION_BREAK = FIRST_SECTION_BREAK;
  protected readonly MAX_SECTION_BREAK = MAX_SECTION_BREAK;

  constructor(private documentTypeServiceProvider: DocumentTypeServiceFactory) {
    super();
    console.log('Creating AttachmentSectionComponent');
  }

  public static override buildForm(formGroup: UntypedFormGroup, section?: ISection): void {

    formGroup.get('sourceType').setValue(SectionSourceType.User);
    formGroup.get('referenceId').setValue(section?.referenceId ?? 'FILE_1');

    const config = section?.config
      ? FileConfig.clone(section.config as FileConfig)
      : new FileConfig(false, { extension: ['doc', 'docx', 'pdf'] });
    formGroup.get('required').setValue(config.required);

    const fileAgeOption = FileAgeRestrictionComponent.fileAgeOptions.find((fao: FileAge) => fao.days === config.filters.fileMaxAgeDays);
    const defaultFileAgeOption = FileAgeRestrictionComponent.fileAgeOptions.find((fao: FileAge) => 0 === fao.days);
    const fileMaxAgeDaysForm = new UntypedFormControl(fileAgeOption?.days ?? defaultFileAgeOption?.days ?? 0);

    const configForm = new FormGroup({
      required: new FormControl({ value: config.required ?? false, disabled: false }),
      filters: new FormGroup({
        extension: new FormControl([...config.filters.extension]),
        documentTypeId: new FormControl(config.filters.documentTypeId ? [...config.filters.documentTypeId] : undefined),
        fileMaxAgeDays: fileMaxAgeDaysForm,
      }),
      optional: new FormControl({ value: config.optional ?? false, disabled: false }),
      multiple: new FormControl(config.multiple ?? false),
      attachmentUploadSource: new FormControl(config.attachmentUploadSource ?? BullhornNamespace.name),
      attachmentSourceEntities: new FormControl(config.attachmentSourceEntities?.length ? [...config.attachmentSourceEntities] : []),
      attachmentAnonymise: new FormControl(config.attachmentAnonymise ?? false),
      saveBackToDataSource: new FormControl(config.saveBackToDataSource ?? true),
    });
    formGroup.setControl('config', configForm);
  }

  ngOnInit(): void {
    const documentTypeService = this.documentTypeServiceProvider.getService(this.namespace);
    this.outputFileType = documentTypeService.getDocumentTypeLabel();
    this.recordTerm = documentTypeService.getRecordTerm();
    this.normalizeAttachmentUploadSource();
    this.required = this.fileSection.controls['required'].value || this._forceEnabled;
    this.forceAttachmentRequired(this._forceEnabled);

    this.fileConfig.get('required').valueChanges.subscribe(value => {
      this.fileSection.get('required').setValue(value);
    });
  }

  onDocumentAttachmentOptionalChange(): void {
    this.fileConfig.patchValue({ optional: !this.fileConfig.get('optional')?.value });
    this.fileConfig.markAsDirty();
  }

  onFileAttachmentOpened(): void {
    if (!this.fileConfig.get('required')?.value && !this._forceEnabled) {
      this.fileConfig.get('required')?.setValue(true);
      this.fileConfig.markAsDirty();
    }
  }

  onFileAttachmentClosed(): void {
    if (this.fileConfig.get('required')?.value && !this._forceEnabled) {
      this.fileConfig.get('required')?.setValue(false);
      this.fileConfig.markAsDirty();
    }
  }

  scrollToFileAttachments(): void {
    const el = document.getElementById('attachmentRequiredCheckbox');
    el.scrollIntoView({ behavior: 'smooth' });
  }

  private forceAttachmentRequired(forceRequired: boolean) {
    if (forceRequired) {
      this.fileSection.controls['required'].setValue(true);
      this.fileConfig.get('required')?.setValue(true);
      this.fileConfig.get('required')?.disable();
      this.fileConfig.get('optional')?.setValue(false);
      this.fileConfig.get('optional')?.disable();
    } else {
      this.fileConfig.get('required')?.enable();
      this.fileConfig.get('optional')?.enable();
    }
  }

  private normalizeAttachmentUploadSource(): void {
    const sourceControl = this.fileConfig?.get('attachmentUploadSource');
    if (!sourceControl) {
      return;
    }

    const defaultNamespaceSource = this.getDefaultUploadSourceForNamespace();
    const allowedSources = [defaultNamespaceSource, LocalFileNamespace.name, 'both'];
    if (!allowedSources.includes(sourceControl.value)) {
      sourceControl.setValue(defaultNamespaceSource);
    }
  }

  private getDefaultUploadSourceForNamespace(): string {
    if (this.namespace === Namespace.salesforce) {
      return SalesforceNamespace.name;
    }
    return BullhornNamespace.name;
  }
}
